import { create } from 'zustand';

interface PlaybackState {
  currentTime: number;
  playing: boolean;
  rate: number;
  loopEnabled: boolean;
  loopRange: [number, number];
  droppedFrames: number;
  setTime(time: number): void;
  setPlaying(playing: boolean): void;
  toggle(): void;
  step(frames: number, fps: number): void;
  setRate(rate: number): void;
  setLoop(enabled: boolean, range?: [number, number]): void;
}

export const usePlaybackStore = create<PlaybackState>((set, get) => ({
  currentTime: 0,
  playing: false,
  rate: 1,
  loopEnabled: false,
  loopRange: [0, 5],
  droppedFrames: 0,
  setTime: (time) => set({ currentTime: Math.max(0, time) }),
  setPlaying: (playing) => set({ playing }),
  toggle: () => set({ playing: !get().playing }),
  step: (frames, fps) => set({ currentTime: Math.max(0, get().currentTime + frames / fps), playing: false }),
  setRate: (rate) => set({ rate }),
  setLoop: (loopEnabled, range) => set({ loopEnabled, ...(range ? { loopRange: range } : {}) })
}));

let animationFrame = 0;
let previous = 0;

export function startPlaybackClock(getDuration: () => number, onStarted?: () => void, onStopped?: () => void) {
  const tick = (now: number) => {
    const state = usePlaybackStore.getState();
    if (!state.playing) { previous = 0; animationFrame = 0; onStopped?.(); return; }
    if (!previous) { previous = now; onStarted?.(); }
    let next = state.currentTime + Math.min(0.1, (now - previous) / 1000) * state.rate;
    previous = now;
    const duration = getDuration();
    if (next <= 0 && state.rate < 0) {
      usePlaybackStore.setState({ currentTime: 0, playing: false });
      previous = 0;
      onStopped?.();
      return;
    }
    next = Math.max(0, next);
    if (state.loopEnabled && next >= state.loopRange[1]) next = state.loopRange[0];
    if (next >= duration) {
      usePlaybackStore.setState({ currentTime: duration, playing: false });
      previous = 0;
      onStopped?.();
      return;
    }
    usePlaybackStore.setState({ currentTime: next });
    animationFrame = requestAnimationFrame(tick);
  };
  const unsubscribe = usePlaybackStore.subscribe((state, previousState) => {
    if (state.playing && !previousState.playing && !animationFrame) animationFrame = requestAnimationFrame(tick);
    if (!state.playing && previousState.playing) { cancelAnimationFrame(animationFrame); animationFrame = 0; previous = 0; onStopped?.(); }
  });
  return () => { unsubscribe(); cancelAnimationFrame(animationFrame); animationFrame = 0; };
}
